import { Group, Select, SelectProps, createStyles } from "@mantine/core";
import React, { forwardRef, useId } from "react";
import { RpiAlert } from "@rpi/openapi-core";
import { IconV3 } from "@rpi/openapi-core";
import { Tooltip, tokens } from "@blurb/codex-react";
import { Body, Label } from "../Typography";

const useStyles = createStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[2],
  },
  dropdown: {
    boxShadow: tokens.shadow.raised,
    border: "none",
  },
  rightSection: {
    pointerEvents: "none",
  },
  item: {
    color: tokens.color.semantic.text.bold,
    "&[data-selected]": {
      backgroundColor: tokens.color.semantic.bg.neutral,
      "&:hover": {
        backgroundColor: tokens.color.semantic.bg.inputHover,
      },
    },
  },
  optionalText: {
    color: tokens.color.semantic.text.subtle,
  },
  input: {
    borderRadius: tokens.radius.sm,
    backgroundColor: tokens.color.semantic.bg.input,
    padding: tokens.spacing[2],
    paddingRight: "24px" /* custom value to offset the icon */,
    color: tokens.color.semantic.text.bold,
    fontSize: tokens.font.size.base,
    fontFamily: tokens.font.family.sans,
    "font-weight": tokens.font.weight.normal,
    lineHeight: tokens.font.lineHeight.normal,
    borderColor: tokens.color.semantic.border.subtle,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    height: "unset",
    "&::placeholder": {
      color: tokens.color.semantic.text.subtle,
    },
    "&:hover": {
      background: tokens.color.semantic.bg.inputHover,
    },
    "&:focus-visible": {
      borderColor: tokens.color.semantic.border.subtle,
      outline: `2px solid ${tokens.color.semantic.border.focus}`,
    },
    "&:disabled": {
      backgroundColor: tokens.color.semantic.bg.neutralDisabled,
      color: tokens.color.semantic.text.disabled,
      opacity: "unset",
    },
    "&[data-error]": {
      borderColor: tokens.color.semantic.border.danger,
    },
  },
  errorContainer: {
    display: "flex",
    gap: 4,
    alignItems: "start",
  },
  errorIcon: {
    lineHeight: "inherit",
  },
  separator: {
    borderTopColor: tokens.color.semantic.border.faint,
  },
  separatorLabel: {
    color: tokens.color.semantic.text.bold,
    "font-weight": tokens.font.weight.bold,
    fontSize: tokens.font.size.base,
    fontFamily: tokens.font.family.sans,
    "&::after": {
      display: "none",
    },
  },
});

interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  value: string;
  "data-selected"?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

const useItemStyles = createStyles(() => ({
  item: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: 8,
  },
  text: {
    flex: 1,
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
}));

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ label, "data-selected": dataSelected, disabled, disabledReason, ...others }, ref) => {
    const { classes } = useItemStyles();
    const isSelected = dataSelected || false;

    const item = (
      <div ref={ref} aria-disabled={disabled || undefined} {...others}>
        <div className={classes.item}>
          <Body className={classes.text} weight={isSelected ? "bold" : "normal"}>
            {label}
          </Body>
          {isSelected && <IconV3 icon="check" color={tokens.color.semantic.icon.bold} />}
        </div>
      </div>
    );

    // A disabled Mantine item swallows pointer events, so the tooltip needs a
    // wrapper to catch hover.
    if (disabledReason) {
      return (
        <Tooltip content={disabledReason}>
          <div>{item}</div>
        </Tooltip>
      );
    }

    return item;
  }
);

SelectItem.displayName = "SelectItem";

interface RpaDropdownProps extends SelectProps {
  optional?: boolean;
  withinPortal?: boolean;
}

export const RpaDropdown: React.FC<RpaDropdownProps> = ({
  label,
  optional = false,
  error,
  withinPortal = false,
  ...props
}) => {
  const { classes } = useStyles();
  const inputId = useId();

  return (
    <div className={classes.container}>
      {label && (
        <Group position="apart">
          <Label as="label" htmlFor={inputId}>
            {label}
          </Label>
          {optional && (
            <Body className={classes.optionalText} size="sm">
              Optional
            </Body>
          )}
        </Group>
      )}
      <Select
        id={inputId}
        classNames={{
          input: classes.input,
          dropdown: classes.dropdown,
          rightSection: classes.rightSection,
          item: classes.item,
          separator: classes.separator,
          separatorLabel: classes.separatorLabel,
        }}
        itemComponent={SelectItem}
        data-error={!!error || undefined}
        withinPortal={withinPortal}
        {...props}
        rightSection={<IconV3 icon="keyboard_arrow_down" />}
      />
      {error && <RpiAlert>{error}</RpiAlert>}
    </div>
  );
};
